import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import { AreaContact } from '../types';
import { areaContactsApi, type AreaContactInput } from '../api/areaContacts';
import { useAuthStore } from './authStore';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('areaContactsStore');

// Module-level subscription handle, same pattern as mapOverlaysStore's
// overlaysUnsub/overlaysKey: a zustand store's own state isn't a safe place
// to stash an Unsubscribe closure across StrictMode double-invocation.
// Keyed on areaId so a repeat subscribe() call for the same area is a
// no-op; switching areas tears down the old listener first.
let contactsUnsub: Unsubscribe | null = null;
let contactsKey: string | null = null;

interface AreaContactsState {
  contacts: AreaContact[];
  loading: boolean;
  error: string | null;

  // The People sheet's live list for one area (plus every '*' entry) — see
  // areaContactsApi.subscribe. Moderator-only at the rules layer; a
  // non-moderator subscribing gets a permission-denied onError and an empty
  // list rather than a thrown error.
  subscribe: (areaId: string) => Unsubscribe;

  create: (areaId: string, input: AreaContactInput) => Promise<string | null>;
  update: (id: string, input: AreaContactInput) => Promise<void>;
  remove: (id: string) => Promise<void>;

  reset: () => void;
}

export const useAreaContactsStore = create<AreaContactsState>((set) => ({
  contacts: [],
  loading: false,
  error: null,

  subscribe: (areaId) => {
    if (contactsUnsub && contactsKey === areaId) return contactsUnsub;
    contactsUnsub?.();
    contactsKey = areaId;
    set({ loading: true, error: null });

    contactsUnsub = areaContactsApi.subscribe(
      areaId,
      (contacts) => set({ contacts, loading: false }),
      (error) => {
        log.warn('subscribe failed:', error);
        set({ loading: false, error: error.message });
      }
    );
    return contactsUnsub;
  },

  create: async (areaId, input) => {
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ error: 'Sign in to add someone.' });
      return null;
    }
    const id = areaContactsApi.newContactId();
    try {
      await areaContactsApi.create(id, areaId, input, user.id);
      return id;
    } catch (error) {
      log.warn('create failed:', error);
      set({ error: (error as Error).message });
      return null;
    }
  },

  update: async (id, input) => {
    await areaContactsApi.update(id, input);
  },

  remove: async (id) => {
    await areaContactsApi.delete(id);
  },

  reset: () => {
    contactsUnsub?.();
    contactsUnsub = null;
    contactsKey = null;
    set({ contacts: [], loading: false, error: null });
  },
}));
